import { Button, Checkbox, Form, Pagination, Select, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "shared/redux/store";
import { ReactComponent as IconPDF } from "../../../assets/images/pdf.svg";
import PayoutInvoicesRow from "./PayoutInvoicesRow";
import "./PayoutInvoices.scss";
import FormSelect from "components/FormSelect/FormSelect";
import { downloadDocument } from "shared/services/Utility";

interface IPayoutInvoices {
  invoiceList: any;
  totalCount: number;
  callbackUpdateInvoice: () => void;
  handlePaginationChange: (pagination: any) => void;
}

const PayoutInvoices: React.FunctionComponent<IPayoutInvoices> = (
  props: IPayoutInvoices
) => {
  const {
    invoiceList,
    totalCount,
    callbackUpdateInvoice,
    handlePaginationChange,
  } = props;

  const payoutReq: any = useSelector((state: RootState) => state.payoutReq);
  const { appColors }: any = useSelector((state: RootState) => state.common);
  const { tenantInfo }: any = useSelector((state: RootState) => state.tenant);
  const [formInvoice] = Form.useForm();

  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);

  const columns: any = [
    {
      title: "Invoice ID",
      dataIndex: "invoiceId",
      key: "invoiceId",
    },
    {
      title: "Invoice Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
    },
    {
      title: "Invoice No.",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      showTooltip: true,
    },
    {
      title: "IRN",
      dataIndex: "irn",
      key: "irn",
    },
    {
      title: "Connector Name",
      dataIndex: "connectorName",
      key: "connectorName",
      width: 150,
      showTooltip: true,
    },
    {
      title: "Connector ID",
      dataIndex: "connectorId",
      key: "ConnectorID",
      width: 150,
    },
    {
      title: "Invoice Amount",
      dataIndex: "invoiceAmount",
      key: "invoiceAmount",
      isAmount: true,
    },
    {
      title: "TDS",
      dataIndex: "tds",
      key: "tds",
      isAmount: true,
    },
    {
      title: "Net Payable Amount",
      dataIndex: "netPayableAmount",
      key: "netPayableAmount",
      isAmount: true,
    },
    {
      title: "Invoice Upload Date",
      dataIndex: "InvoiceUploadDate",
      key: "InvoiceUploadDate",
      render: (record: any) => {
        return record.actionList?.map((action: any) => {
          return <span>{action?.date}</span>;
        });
      },
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      render: (record: any) => {
        return record.actionList?.map((action: any) => {
          let value = action?.status?.toLowerCase();
          if ((value?.length ?? 0) == 0) {
            return <div style={{ height: "15px" }} />;
          }
          return (
            <span
              style={{ color: action?.status == "REJECTED" ? "#E12121" : "" }}
            >
              {value.charAt(0).toUpperCase() + value.slice(1)}
            </span>
          );
        });
      },
    },
    {
      title: "Comment",
      dataIndex: "Comment",
      key: "Comment",
      width: 250,
      render: (record: any) => {
        return record.actionList?.map((action: any) => {
          return (
            <Tooltip title={action?.comment}>
              <span className="span-single-line">
                {action?.comment}
              </span>
            </Tooltip>
          );
        });
      },
    },
    {
      title: "Signed Invoice",
      dataIndex: "SignedInvoice",
      key: "SignedInvoice",
      className: "h-center",
      fixed: "right",
      render: (record: any) => {
        return record.actionList?.map((action: any) => {
          if ((action?.signedDocumentId?.length ?? 0) == 0) {
            return <div style={{ height: "20px" }} />;
          }
          return (
            <Button
              className="custome-btn-icon-play"
              style={{ "--bgColor": appColors?.appPrimaryColor ?? "" } as any}
              size="small"
              type="text"
              onClick={() =>
                downloadDocument({
                  businessDocumentId: action?.signedDocumentId,
                })
              }
              icon={<IconPDF />}
            />
          );
        });
      },
    },
    tenantInfo.connectorInvoicingEnabled && {
      title: "Action",
      dataIndex: "Action",
      key: "Action",
      className: "h-center",
      fixed: "right",
      width: 150,
      render: (record: any, callback, status) => {
        return record.actionList?.map((action: any) => {
          if (action?.status !== "UPLOADED") {
            return <div style={{ height: "22px" }} />;
          }
          return (
            <div style={{ width: "150px", marginBlock: "-6px" }}>
              <Select
                size="large"
                className="custom-select"
                placeholder={"Select Status"}
                value={status}
                onChange={(value) => {
                  callback(value);
                }}
              >
                {[
                  { key: "APPROVED", value: "Approved" },
                  { key: "REJECTED", value: "Rejected" },
                ]?.map((option: any, i: number) => (
                  <Select.Option key={i} value={option.key}>
                    {option.value}
                  </Select.Option>
                ))}
              </Select>
            </div>
          );
        });
      },
    },
  ].filter((item) => item);

  return (
    <>
      <div className="payout-invoices-main">
        <div className="payout-invoices-table-header">
          {columns?.map((data: any, index) => {
            let styleExtra: any = {};
            if (data?.fixed == "right") {
              let right = 0;
              if (columns.length - 1 != index) {
                for (let i = index + 1; i < columns.length; i++) {
                  right = right + (columns.at(i)?.width ?? 120);
                }
              }
              let sty = {};
              if (columns.at(index - 1)?.fixed !== "right") {
                sty = { borderLeft: "1px solid #DFE0EB" };
              }
              styleExtra = {
                position: "sticky",
                right: right,
                zIndex: 10,
                ...sty,
              };
            }
            return (
              <div
                style={{
                  flex: "none",
                  width: data?.width ?? "120px",
                  ...styleExtra,
                }}
                className={data?.className ?? ""}
              >
                {data?.type == "checkbox" ? (
                  <Checkbox
                    checked={selectedInvoices?.length == invoiceList?.length}
                    onChange={(e: any) => {
                      if (e.target.checked) {
                        setSelectedInvoices(
                          invoiceList?.map((invoice: any) => invoice?.invoiceId)
                        );
                      } else {
                        setSelectedInvoices([]);
                      }
                    }}
                  >
                    {data.title}
                  </Checkbox>
                ) : (
                  data.title
                )}
              </div>
            );
          })}
        </div>
        {(invoiceList?.length ?? 0) == 0 ? (
          <div style={{ height: "100px" }} />
        ) : (
          invoiceList?.map((invoice: any) => {
            return (
              <PayoutInvoicesRow
                formInvoice={formInvoice}
                invoice={invoice}
                columns={columns}
                selectedInvoices={selectedInvoices}
                setSelectedInvoices={setSelectedInvoices}
                callbackUpdateInvoice={callbackUpdateInvoice}
              />
            );
          })
        )}
      </div>
      <div className="payout-invoices-footer">
        <div>
          {tenantInfo.connectorInvoicingEnabled ? "" : "Invoicing is disabled"}
        </div>
        <Pagination
          current={payoutReq.pageNo}
          pageSize={payoutReq.pageSize}
          total={totalCount}
          onChange={(page: number, pageSize: number) => {
            handlePaginationChange({ pageSize, current: page });
          }}
        />
      </div>
    </>
  );
};

export default PayoutInvoices;
